import { API_KEY_EXPIRY_MAX_DAYS, API_KEY_NAME_MAX } from "@mfa/v5-contracts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useId, useState } from "react";
import { FormattedMessage, type IntlShape, useIntl } from "react-intl";
import { Link } from "react-router-dom";
import { useAppContext } from "../../app/AppContext";
import type { ApiKey, ApiKeyAccess, ApiKeyCreateInput } from "../../core/types";
import { useUnsavedWork } from "../../core/unsaved-work";
import { EmptyState, InlineApiError, sharedStyles } from "../../shared/Shared";
import pageStyles from "../../styles/Page.module.css";
import styles from "./ProfilePage.module.css";

export const API_KEYS_QUERY_KEY = ["api-keys"] as const;

/** The key just issued. Its secret lives only here, in memory, until the person dismisses it. */
interface IssuedKey {
  id: string;
  name: string;
  secret: string;
}

/**
 * The profile page's "API keys" section (V6 plan §13.3.1 "Keys", row H12): the account's keys
 * on this site over `apiKeys.list`, `create` and `revoke`, the same procedures any client
 * calls. A new key's secret is shown once, with a copy button, and is never fetched again:
 * the list only ever carries a key's prefix.
 */
export function ApiKeysSection() {
  const intl = useIntl();
  const { api } = useAppContext();
  const queryClient = useQueryClient();
  const titleId = useId();
  const keysQuery = useQuery({ queryKey: API_KEYS_QUERY_KEY, queryFn: () => api.listApiKeys() });
  // The same cache entry the Builder gateway reads.
  const projectsQuery = useQuery({ queryKey: ["projects"], queryFn: () => api.listProjects() });
  const projectNames = new Map(
    (projectsQuery.data?.projects ?? []).map((project) => [project.id, project.name]),
  );
  const [issued, setIssued] = useState<IssuedKey | null>(null);
  // A new key's secret shows once and a reload loses it for good: while it is on screen it holds a
  // newer release's automatic reload back (core/unsaved-work.ts), and lets go once it is
  // dismissed with Done or its key is revoked.
  useUnsavedWork("profile.apiKeys.secret", issued !== null);

  const refreshKeys = () => queryClient.invalidateQueries({ queryKey: API_KEYS_QUERY_KEY });
  const create = useMutation({
    mutationFn: (input: ApiKeyCreateInput) => api.createApiKey(input),
    onSuccess: async ({ key, secret }) => {
      setIssued({ id: key.id, name: key.name, secret });
      await refreshKeys();
    },
  });
  const revoke = useMutation({
    mutationFn: (id: string) => api.revokeApiKey(id),
    onSuccess: async (_result, id) => {
      // A secret still on screen for a key that no longer works is only noise.
      setIssued((current) => (current?.id === id ? null : current));
      await refreshKeys();
    },
  });

  const onRevoke = (key: ApiKey) => {
    if (window.confirm(intl.formatMessage({ id: "apiKeys.revokeConfirm" }, { name: key.name }))) {
      revoke.mutate(key.id);
    }
  };

  const keys = keysQuery.data?.keys ?? [];

  return (
    <section className={pageStyles.card} aria-labelledby={titleId}>
      <h2 id={titleId} className={pageStyles.sectionTitle}>
        <FormattedMessage id="apiKeys.title" />
      </h2>
      <p className={styles.sectionIntro}>
        <FormattedMessage id="apiKeys.intro" />{" "}
        <FormattedMessage
          id="apiKeys.reference"
          values={{
            link: (
              <Link to="/developers">
                <FormattedMessage id="developers.eyebrow" />
              </Link>
            ),
          }}
        />
      </p>

      {issued ? (
        <IssuedSecret key={issued.id} issued={issued} onDone={() => setIssued(null)} />
      ) : null}

      {keysQuery.isPending ? (
        <p role="status">
          <FormattedMessage id="common.loading" />
        </p>
      ) : keysQuery.isError ? (
        <InlineApiError error={keysQuery.error} />
      ) : keys.length === 0 ? (
        <EmptyState>
          <FormattedMessage id="apiKeys.empty" />
        </EmptyState>
      ) : (
        <>
          <div className={styles.tableFrame}>
            <table className={styles.table}>
              <caption className={styles.visuallyHidden}>
                <FormattedMessage id="apiKeys.caption" />
              </caption>
              <thead>
                <tr>
                  <th scope="col">
                    <FormattedMessage id="apiKeys.col.name" />
                  </th>
                  <th scope="col">
                    <FormattedMessage id="apiKeys.col.prefix" />
                  </th>
                  <th scope="col">
                    <FormattedMessage id="apiKeys.col.access" />
                  </th>
                  <th scope="col">
                    <FormattedMessage id="apiKeys.col.project" />
                  </th>
                  <th scope="col">
                    <FormattedMessage id="apiKeys.col.created" />
                  </th>
                  <th scope="col">
                    <FormattedMessage id="apiKeys.col.lastUsed" />
                  </th>
                  <th scope="col">
                    <FormattedMessage id="apiKeys.col.expires" />
                  </th>
                  <th scope="col">
                    <span className={styles.visuallyHidden}>
                      <FormattedMessage id="apiKeys.col.actions" />
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {keys.map((key) => (
                  <ApiKeyRow
                    key={key.id}
                    apiKey={key}
                    projectName={key.projectId ? projectNames.get(key.projectId) : undefined}
                    revoking={revoke.isPending && revoke.variables === key.id}
                    onRevoke={() => onRevoke(key)}
                  />
                ))}
              </tbody>
            </table>
          </div>
          <p className={styles.hint}>
            <FormattedMessage id="apiKeys.lastUsedNote" />
          </p>
        </>
      )}
      <InlineApiError error={revoke.error} />

      <CreateKeyForm
        projects={projectsQuery.data?.projects ?? []}
        projectsState={
          projectsQuery.isPending ? "loading" : projectsQuery.isError ? "failed" : "ready"
        }
        onRetryProjects={() => void projectsQuery.refetch()}
        // One secret on screen at a time: a second key would replace it before it was saved.
        locked={issued !== null}
        busy={create.isPending}
        error={create.error}
        onCreate={(input, reset) => create.mutate(input, { onSuccess: reset })}
      />
    </section>
  );
}

function ApiKeyRow({
  apiKey,
  projectName,
  revoking,
  onRevoke,
}: {
  apiKey: ApiKey;
  projectName: string | undefined;
  revoking: boolean;
  onRevoke: () => void;
}) {
  const intl = useIntl();
  const expired = apiKey.expiresAt !== null && Date.parse(apiKey.expiresAt) <= Date.now();
  return (
    <tr>
      <td className={styles.keyName}>{apiKey.name}</td>
      <td>{apiKey.prefix ? <code className={styles.prefix}>{apiKey.prefix}…</code> : "—"}</td>
      <td>
        <span className={pageStyles.badge}>
          <FormattedMessage id={`apiKeys.access.${apiKey.access}`} />
        </span>
      </td>
      <td className={styles.keyProject}>
        {apiKey.projectId === null ? (
          <FormattedMessage id="apiKeys.allProjects" />
        ) : (
          (projectName ?? (
            <FormattedMessage
              id="apiKeys.unknownProject"
              values={{ id: apiKey.projectId.slice(0, 8) }}
            />
          ))
        )}
      </td>
      <td>{formatDay(intl, apiKey.createdAt)}</td>
      <td>
        {apiKey.lastUsedAt ? (
          relativeTime(intl, apiKey.lastUsedAt)
        ) : (
          <FormattedMessage id="apiKeys.never" />
        )}
      </td>
      <td>
        {apiKey.expiresAt === null ? (
          <FormattedMessage id="apiKeys.never" />
        ) : expired ? (
          <span className={styles.expired}>
            <FormattedMessage
              id="apiKeys.expired"
              values={{ date: formatDay(intl, apiKey.expiresAt) }}
            />
          </span>
        ) : (
          formatDay(intl, apiKey.expiresAt)
        )}
      </td>
      <td>
        <button
          className={`${sharedStyles.button} ${sharedStyles.buttonSecondary} ${styles.revoke}`}
          type="button"
          disabled={revoking}
          aria-label={intl.formatMessage({ id: "apiKeys.revokeLabel" }, { name: apiKey.name })}
          onClick={onRevoke}
        >
          <FormattedMessage id={revoking ? "apiKeys.revoking" : "apiKeys.revoke"} />
        </button>
      </td>
    </tr>
  );
}

/** The secret of the key just issued, once: a read-only field to select, a copy button, a warning. */
function IssuedSecret({ issued, onDone }: { issued: IssuedKey; onDone: () => void }) {
  const intl = useIntl();
  const titleId = useId();
  const [copy, setCopy] = useState<"idle" | "copied" | "failed">("idle");
  const onCopy = async () => {
    try {
      if (!navigator.clipboard) throw new Error("clipboard unavailable");
      await navigator.clipboard.writeText(issued.secret);
      setCopy("copied");
    } catch {
      setCopy("failed");
    }
  };
  return (
    <section className={styles.secretPanel} aria-labelledby={titleId}>
      <h3 id={titleId} className={styles.subTitle}>
        <FormattedMessage id="apiKeys.secret.title" />
      </h3>
      <p className={styles.secretWarning}>
        <FormattedMessage id="apiKeys.secret.warning" />
      </p>
      <div className={styles.secretRow}>
        <input
          className={styles.secret}
          readOnly
          value={issued.secret}
          aria-label={intl.formatMessage({ id: "apiKeys.secret.label" }, { name: issued.name })}
          spellCheck={false}
          onFocus={(event) => event.currentTarget.select()}
        />
        <button className={sharedStyles.button} type="button" onClick={() => void onCopy()}>
          <FormattedMessage
            id={copy === "copied" ? "apiKeys.secret.copied" : "apiKeys.secret.copy"}
          />
        </button>
      </div>
      {copy === "failed" ? (
        <p className={sharedStyles.errorText} role="alert">
          <FormattedMessage id="apiKeys.secret.copyFailed" />
        </p>
      ) : null}
      <button
        className={`${sharedStyles.button} ${sharedStyles.buttonSecondary}`}
        type="button"
        onClick={onDone}
      >
        <FormattedMessage id="apiKeys.secret.done" />
      </button>
    </section>
  );
}

function CreateKeyForm({
  projects,
  projectsState,
  onRetryProjects,
  locked,
  busy,
  error,
  onCreate,
}: {
  projects: { id: string; name: string }[];
  /** The project list behind the scope choice: loading, failed (a retry is offered) or ready. */
  projectsState: "loading" | "failed" | "ready";
  onRetryProjects: () => void;
  /** A new key's secret is still on screen: no second key until the person has chosen Done. */
  locked: boolean;
  busy: boolean;
  error: unknown;
  onCreate: (input: ApiKeyCreateInput, reset: () => void) => void;
}) {
  const intl = useIntl();
  const titleId = useId();
  const [name, setName] = useState("");
  // A key name typed and not yet created holds a newer release's automatic reload back
  // (core/unsaved-work.ts); a created key resets the form and lets go, a refused one keeps it.
  useUnsavedWork("profile.apiKeys.name", name.trim() !== "");
  const [access, setAccess] = useState<ApiKeyAccess>("read");
  const [projectId, setProjectId] = useState("");
  const [expiry, setExpiry] = useState("");
  const days = expiry.trim() === "" ? null : Number(expiry);
  const daysValid =
    days === null || (Number.isInteger(days) && days >= 1 && days <= API_KEY_EXPIRY_MAX_DAYS);
  const ready = name.trim().length > 0 && daysValid && !busy && !locked;

  const reset = () => {
    setName("");
    setAccess("read");
    setProjectId("");
    setExpiry("");
  };
  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!ready) return;
    onCreate(
      {
        name: name.trim(),
        access,
        ...(projectId ? { projectId } : {}),
        ...(days !== null ? { expiresInDays: days } : {}),
      },
      reset,
    );
  };

  return (
    <form className={styles.createForm} aria-labelledby={titleId} onSubmit={onSubmit}>
      <h3 id={titleId} className={styles.subTitle}>
        <FormattedMessage id="apiKeys.create.title" />
      </h3>
      <div className={styles.fieldGrid}>
        <label className={pageStyles.field}>
          <FormattedMessage id="apiKeys.field.name" />
          <input
            value={name}
            required
            maxLength={API_KEY_NAME_MAX}
            placeholder={intl.formatMessage({ id: "apiKeys.field.namePlaceholder" })}
            onChange={(event) => setName(event.target.value)}
          />
        </label>
        <label className={pageStyles.field}>
          <FormattedMessage id="apiKeys.field.project" />
          <select
            value={projectId}
            disabled={projectsState === "loading"}
            aria-describedby={`${titleId}-projects-note`}
            onChange={(event) => setProjectId(event.target.value)}
          >
            <option value="">{intl.formatMessage({ id: "apiKeys.allProjects" })}</option>
            {projects.map((project) => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </select>
          <span id={`${titleId}-projects-note`} className={styles.hint}>
            {projectsState === "loading" ? (
              <span role="status">
                <FormattedMessage id="apiKeys.projects.loading" />
              </span>
            ) : projectsState === "failed" ? (
              <span role="alert">
                <FormattedMessage id="apiKeys.projects.failed" />{" "}
                <button
                  className={`${sharedStyles.button} ${sharedStyles.buttonSecondary}`}
                  type="button"
                  onClick={onRetryProjects}
                >
                  <FormattedMessage id="apiKeys.projects.retry" />
                </button>
              </span>
            ) : projects.length === 0 ? (
              <FormattedMessage id="apiKeys.projects.none" />
            ) : null}
          </span>
        </label>
        <div className={pageStyles.field}>
          <label htmlFor={`${titleId}-expiry`}>
            <FormattedMessage id="apiKeys.field.expiry" />
          </label>
          <input
            id={`${titleId}-expiry`}
            type="number"
            inputMode="numeric"
            min={1}
            max={API_KEY_EXPIRY_MAX_DAYS}
            step={1}
            value={expiry}
            aria-invalid={!daysValid}
            aria-describedby={`${titleId}-expiry-hint`}
            onChange={(event) => setExpiry(event.target.value)}
          />
          <span id={`${titleId}-expiry-hint`} className={styles.hint}>
            <FormattedMessage id="apiKeys.field.expiryHint" />
          </span>
        </div>
      </div>
      <fieldset className={styles.accessChoices}>
        <legend>
          <FormattedMessage id="apiKeys.field.access" />
        </legend>
        {(["read", "write"] as const).map((level) => (
          <label key={level} className={styles.accessChoice}>
            <input
              type="radio"
              name={`${titleId}-access`}
              value={level}
              checked={access === level}
              onChange={() => setAccess(level)}
            />
            <FormattedMessage
              id={level === "read" ? "apiKeys.field.accessRead" : "apiKeys.field.accessWrite"}
            />
          </label>
        ))}
      </fieldset>
      <div>
        <button
          className={sharedStyles.button}
          type="submit"
          disabled={!ready}
          aria-describedby={locked ? `${titleId}-locked` : undefined}
        >
          <FormattedMessage id={busy ? "common.creating" : "apiKeys.create.submit"} />
        </button>
        {locked ? (
          <p id={`${titleId}-locked`} className={styles.hint} role="status">
            <FormattedMessage id="apiKeys.create.lockedHint" />
          </p>
        ) : null}
      </div>
      <InlineApiError error={error} />
    </form>
  );
}

/** A calendar day in the viewer's locale ("Oct 22, 2026"). */
function formatDay(intl: IntlShape, iso: string): string {
  return intl.formatDate(iso, { dateStyle: "medium" });
}

/** "5 minutes ago", "yesterday", "3 days ago": the unit scales with the distance. */
function relativeTime(intl: IntlShape, iso: string): string {
  const diffMs = Date.parse(iso) - Date.now();
  const minutes = Math.round(diffMs / 60_000);
  if (Math.abs(minutes) < 60)
    return intl.formatRelativeTime(minutes, "minute", { numeric: "auto" });
  const hours = Math.round(diffMs / 3_600_000);
  if (Math.abs(hours) < 24) return intl.formatRelativeTime(hours, "hour", { numeric: "auto" });
  return intl.formatRelativeTime(Math.round(diffMs / 86_400_000), "day", { numeric: "auto" });
}
